import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ChevronRight, ChevronUp, ChevronDown, X, Filter, Download, Search, SortAsc, SortDesc, Truck, Users } from 'lucide-react';
import { apiCallJson, apiCall } from './Api';
import { useAuth } from './contexts/AuthContext';
import SchedulerWidget from './SchedulerWidget';


const HierarchicalTable = () => {
  const { isCustomer } = useAuth();
  const [data, setData] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [loadingNodes, setLoadingNodes] = useState(new Set());
  // Per-node overlay state. Map<nodeKey, 'vendor'|'customer'>. Setting an
  // overlay on a node changes the level used when its children are fetched.
  const [nodeOverlay, setNodeOverlay] = useState(new Map());
  
  // Initialize from URL or defaults
  const getInitialStateFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      rootLevel: urlParams.get('group_by') || 'customer',
      sortBy: urlParams.get('sort_by') || 'total_messages',
      sortOrder: urlParams.get('sort_order') || 'desc',
      rootLimit: parseInt(urlParams.get('limit')) || 100,
      rootOffset: parseInt(urlParams.get('offset')) || 0
    };
  };
  
  const initialState = getInitialStateFromURL();
  
  const [rootLevel, setRootLevel] = useState(initialState.rootLevel);
  const [sortBy, setSortBy] = useState(initialState.sortBy);
  const [sortOrder, setSortOrder] = useState(initialState.sortOrder);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Per-node child sort state: { [parentNodeKey]: { sortBy, sortOrder } }
  const [childSortState, setChildSortState] = useState({});

  // Resizable hierarchy column
  const [hierarchyColWidth, setHierarchyColWidth] = useState(280);
  const colResizing = useRef(false);
  const colStartX = useRef(0);
  const colStartW = useRef(280);
  
  // Pagination state for root data
  const [rootLimit, setRootLimit] = useState(initialState.rootLimit);
  const [rootOffset, setRootOffset] = useState(initialState.rootOffset);
  const [totalRootRecords, setTotalRootRecords] = useState(0);

  // If the URL persisted a forbidden view (vendor, account_manager) for a
  // customer-scoped user (e.g. shared link), reset to customer view.
  useEffect(() => {
    if (isCustomer && (rootLevel === 'vendor' || rootLevel === 'account_manager')) {
      setRootLevel('customer');
    }
  }, [isCustomer, rootLevel]);

  // Likewise, snap sortBy if it came in pointing at a sensitive column.
  useEffect(() => {
    if (isCustomer && (sortBy === 'cost' || sortBy === 'profit_margin' || sortBy === 'fake_dlr_count')) {
      setSortBy('total_messages');
    }
  }, [isCustomer, sortBy]);
  
  const [showDetailPopup, setShowDetailPopup] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [filters, setFilters] = useState(() => {
    // First try URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const timeRange = urlParams.get('time_range');
    const startDate = urlParams.get('start_date');
    const endDate = urlParams.get('end_date');
    
    if (timeRange || (startDate && endDate)) {
      return {
        time_range: timeRange || 'last_7_days',
        start_date: startDate || '',
        end_date: endDate || ''
      };
    }
    
    // Fallback to localStorage
    const saved = localStorage.getItem('etl_date_range');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          time_range: parsed.preset || parsed.time_range || 'last_7_days',
          start_date: parsed.start_date || '',
          end_date: parsed.end_date || ''
        };
      } catch (e) {
        return {
          time_range: 'last_7_days',
          start_date: '',
          end_date: ''
        };
      }
    }
    return {
      time_range: 'last_7_days',
      start_date: '',
      end_date: ''
    };
  });

  // ─── Column resize handlers ─────────────────────────
  const onColResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    colResizing.current = true;
    colStartX.current = e.clientX;
    colStartW.current = hierarchyColWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev) => {
      if (!colResizing.current) return;
      const delta = ev.clientX - colStartX.current;
      const newW = Math.max(140, Math.min(600, colStartW.current + delta));
      setHierarchyColWidth(newW);
    };
    const onUp = () => {
      colResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [hierarchyColWidth]);

  const HIERARCHY_LEVELS = {
    customer: ['customer_service', 'country', 'mcc_mnc', 'sender_id'],
    vendor: ['vendor_service', 'country', 'mcc_mnc', 'sender_id'],
    account_manager: ['customer', 'vendor'],
    customer_service: ['country', 'mcc_mnc', 'sender_id'],
    vendor_service: ['country', 'mcc_mnc', 'sender_id'],
    country: ['mcc_mnc', 'sender_id'],
    mcc_mnc: ['sender_id'],
    sender_id: []
  };

  // Mirror of backend NATURAL_NEXT — used by getNextLevelForNode below to
  // resolve overlay-aware drill paths client-side.
  const NATURAL_NEXT = {
    customer: 'customer_service',
    customer_service: 'country',
    vendor: 'vendor_service',
    vendor_service: 'country',
    country: 'mcc_mnc',
    mcc_mnc: 'sender_id',
    sender_id: null,
  };

  // Keys we'll send back as parent_context, indexed by level. Used to
  // assemble the WHERE filter for overlay drills.
  const LEVEL_KEY = {
    customer: 'customer_id',
    customer_service: 'customer_service_id',
    vendor: 'vendor_id',
    vendor_service: 'vendor_service_id',
    country: 'country_code',
    sender_id: 'sender_id',
    // mcc_mnc is split into two columns; handled separately below
  };

  const ROOT_LEVEL_OPTIONS = (() => {
    const all = [
      { value: 'customer', label: 'Customer View', description: 'Group by customers first' },
      { value: 'vendor', label: 'Vendor View', description: 'Group by vendors first' },
      { value: 'account_manager', label: 'Account Manager View', description: 'Group by account managers (customer/vendor split)' },
      { value: 'country', label: 'Geographic View', description: 'Group by countries first' },
      { value: 'mcc_mnc', label: 'Network View', description: 'Group by networks first' },
      { value: 'sender_id', label: 'Sender ID View', description: 'Group by sender_ids first' }
    ];
    if (isCustomer) {
      const blocked = new Set(['vendor', 'account_manager']);
      return all.filter((o) => !blocked.has(o.value));
    }
    return all;
  })();

  useEffect(() => {
    fetchRootData();
    setExpandedNodes(new Set());
    setChildSortState({});
  }, [rootLevel, filters, sortBy, sortOrder, rootLimit, rootOffset]);

  // Update URL when state changes (write-only)
  useEffect(() => {
    const urlParams = new URLSearchParams();
    
    if (filters.time_range) urlParams.set('time_range', filters.time_range);
    if (filters.start_date) urlParams.set('start_date', filters.start_date);
    if (filters.end_date) urlParams.set('end_date', filters.end_date);
    if (rootLevel) urlParams.set('group_by', rootLevel);
    if (sortBy) urlParams.set('sort_by', sortBy);
    if (sortOrder) urlParams.set('sort_order', sortOrder);
    if (rootLimit) urlParams.set('limit', rootLimit.toString());
    if (rootOffset >= 0) urlParams.set('offset', rootOffset.toString());
    
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.pushState({}, '', newUrl);
  }, [filters.time_range, filters.start_date, filters.end_date, rootLevel, sortBy, sortOrder, rootLimit, rootOffset]);

  useEffect(() => {
    // Save to localStorage with consistent format
    localStorage.setItem('etl_date_range', JSON.stringify({
      preset: filters.time_range,
      time_range: filters.time_range,
      start_date: filters.start_date,
      end_date: filters.end_date
    }));
  }, [filters]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [sortConfig, setSortConfig] = useState({ sort_by: 'submitted_time', sort_order: 'desc' });
  const [searchFilters, setSearchFilters] = useState({
    message_id: '',
    sender_id: '',
    receiver_id: '',
    customer: '',
    vendor: '',
    country: '',
    network: ''
  });

  const fetchDetailedRecords = async (node, page = 1, pageSize = 100, sort = sortConfig, columnFilters = searchFilters) => {
    setLoadingDetail(true);
    try {
      const offset = (page - 1) * pageSize;
      
      // Build parent context from node path
      const parentContext = {};
      const nodePath = node.path || [node.id];


      if(rootLevel == 'account_manager' && nodePath.length==1) {
          if (nodePath[0].startsWith('customer_manager_')) {
            parentContext.customer_account_manager = nodePath[0].replace('customer_manager_', '');
          } else if (nodePath[0].startsWith('vendor_manager_')) {
            parentContext.vendor_account_manager = nodePath[0].replace('vendor_manager_', '');
          }
        }
      
      if (nodePath.length > 1) {
        // Build parent context based on path and root level

        if(rootLevel == 'account_manager') {
          if (nodePath[0].startsWith('customer_manager')) {
            if (nodePath.length >= 1) parentContext.customer_account_manager = nodePath[0].replace('customer_manager_', '');
            if (nodePath.length > 2) parentContext.customer_id = nodePath[1];
            if (nodePath.length > 3) parentContext.customer_service_id = nodePath[2];
            if (nodePath.length > 4) parentContext.country_code = nodePath[3];
            if (nodePath.length >= 5) {
              // Handle mcc_mnc level - split the compound ID
              const [mcc, mnc] = nodePath[4].split('-');
              parentContext.mcc = mcc;
              if (mnc) parentContext.mnc = mnc;
            }
          } else if (nodePath[0].startsWith('vendor_manager')) {
            if (nodePath.length >= 1) parentContext.vendor_account_manager = nodePath[0].replace('vendor_manager_', '');
            if (nodePath.length > 2) parentContext.vendor_id = nodePath[1];
            if (nodePath.length > 3) parentContext.vendor_service_id = nodePath[2];
            if (nodePath.length > 4) parentContext.country_code = nodePath[3];
            if (nodePath.length >= 5) {
              // Handle mcc_mnc level - split the compound ID
              const [mcc, mnc] = nodePath[4].split('-');
              parentContext.mcc = mcc;
              if (mnc) parentContext.mnc = mnc;
            }
          }
      } else if (rootLevel === 'customer') {
          if (nodePath.length >= 1) parentContext.customer_id = nodePath[0];
          if (nodePath.length >= 2) parentContext.customer_service_id = nodePath[1];
          if (nodePath.length >= 3) parentContext.country_code = nodePath[2];
          if (nodePath.length >= 4) {
            // Handle mcc_mnc level - split the compound ID
            const [mcc, mnc] = nodePath[3].split('-');
            parentContext.mcc = mcc;
            if (mnc) parentContext.mnc = mnc;
          }
        } else if (rootLevel === 'vendor') {
          if (nodePath.length >= 1) parentContext.vendor_id = nodePath[0];
          if (nodePath.length >= 2) parentContext.vendor_service_id = nodePath[1];
          if (nodePath.length >= 3) parentContext.country_code = nodePath[2];
          if (nodePath.length >= 4) {
            // Handle mcc_mnc level - split the compound ID
            const [mcc, mnc] = nodePath[3].split('-');
            parentContext.mcc = mcc;
            if (mnc) parentContext.mnc = mnc;
          }
        } else if (rootLevel === 'country') {
          if (nodePath.length >= 1) parentContext.country_code = nodePath[0];
          if (nodePath.length >= 2) {
            // Handle mcc_mnc level
            const [mcc, mnc] = nodePath[1].split('-');
            parentContext.mcc = mcc;
            if (mnc) parentContext.mnc = mnc;
          }
        } else if (rootLevel === 'mcc_mnc') {
          if (nodePath.length >= 1) {
            // Root level is already mcc_mnc
            const [mcc, mnc] = nodePath[0].split('-');
            parentContext.mcc = mcc;
            if (mnc) parentContext.mnc = mnc;
          }
        }
      }
      
      const response = await apiCall('/api/htable/detailed-records', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          node_id: node.id,
          node_level: node.level,
          filters: {
            time_range: filters.time_range,
            ...((filters.start_date && filters.end_date && filters.end_date >= filters.start_date) && {
              start_date: filters.start_date,
              end_date: filters.end_date
            }),
            customer_ids: filters.customer_ids || [],
            vendor_ids: filters.vendor_ids || [],
            country_codes: filters.country_codes || []
          },
          parent_context: parentContext,
          limit: pageSize,
          offset: offset,
          sort_by: sort.sort_by,
          sort_order: sort.sort_order,
          search_message_id: columnFilters.message_id || null,
          search_sender_id: columnFilters.sender_id || null,
          search_receiver_id: columnFilters.receiver_id || null,
          search_customer: columnFilters.customer || null,
          search_vendor: columnFilters.vendor || null,
          search_country: columnFilters.country || null,
          search_network: columnFilters.network || null
        })
      });

      const data = await response.json();
      setDetailData(data);
      setCurrentPage(page);
      setShowDetailPopup(true);
    } catch (error) {
      console.error('Error fetching detailed records:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDetailedSort = (column, order) => {
    if (detailData?.node_info) {
      
    const newSort = { sort_by: column, sort_order: order };
      setSortConfig(newSort);
      const currentNode = {
        id: detailData.node_info.id,
        level: detailData.node_info.level,
        path: detailData.current_path || [detailData.node_info.id] // Add current_path to API response
      };
      fetchDetailedRecords(currentNode, currentPage, pageSize, newSort, searchFilters);
    }
  };

  const handleFilter = (columnFilters) => {
    if (detailData?.node_info) {
      setSearchFilters(columnFilters);  // Update searchFilters state
      setCurrentPage(1);
      const currentNode = {
        id: detailData.node_info.id,
        level: detailData.node_info.level,
        path: detailData.current_path || [detailData.node_info.id] // Add current_path to API response
      };
      fetchDetailedRecords(currentNode, currentPage, pageSize, sortConfig, columnFilters);
    }
  };

  const handlePageChange = (newPage) => {
    if (detailData?.node_info) {
      const currentNode = {
        id: detailData.node_info.id,
        level: detailData.node_info.level,
        path: detailData.current_path || [detailData.node_info.id] // Add current_path to API response
      };
      fetchDetailedRecords(currentNode, newPage, pageSize, sortConfig, searchFilters);
    }
  };

  const handlePageSizeChange = (newSize) => {
    if (detailData?.node_info) {
      setPageSize(newSize);
      setCurrentPage(1);
      const currentNode = {
        id: detailData.node_info.id,
        level: detailData.node_info.level,
        path: detailData.current_path || [detailData.node_info.id] // Add current_path to API response
      };
      fetchDetailedRecords(currentNode, currentPage, newSize, sortConfig, searchFilters);
    }
  };

  const handleRowDoubleClick = (node, event) => {
    event.preventDefault();
    event.stopPropagation();
    fetchDetailedRecords(node);
  };

  const fetchRootData = async () => {
    setLoading(true);
    try {
      const requestBody = {
        filters: {
          time_range: filters.time_range,
          ...((filters.start_date && filters.end_date && filters.end_date >= filters.start_date) && {
            start_date: filters.start_date,
            end_date: filters.end_date
          }),
          customer_ids: filters.customer_ids || [],
          vendor_ids: filters.vendor_ids || [],
          country_codes: filters.country_codes || []
        },
        group_by: [rootLevel],
        sort_by: sortBy,
        sort_order: sortOrder,
        limit: rootLimit,
        offset: rootOffset
      };

      const response = await apiCall('/api/htable/hierarchy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();
      const hierarchyData = result.data || result;
      const total = result.total || hierarchyData.length;
      
      setTotalRootRecords(total);
      
      if (rootLevel === 'account_manager') {
        setData(hierarchyData.map(item => ({ 
          ...item, 
          path: [item.id], 
          level: item.level,
          pathLevels: [item.level],
        })));
      } else {
        setData(hierarchyData.map(item => ({ 
          ...item, 
          path: [item.id], 
          level: rootLevel,
          pathLevels: [rootLevel],
        })));
      }
    } catch (error) {
      console.error('Error fetching root data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDrillDownData = async (node) => {
    try {
      const response = await apiCall('/api/htable/drill-down', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          node_id: node.id,
          node_level: node.level,
          filters: {
            time_range: filters.time_range,
            ...((filters.start_date && filters.end_date && filters.end_date >= filters.start_date) && {
              start_date: filters.start_date,
              end_date: filters.end_date
            }),
            customer_ids: filters.customer_ids || [],
            vendor_ids: filters.vendor_ids || [],
            country_codes: filters.country_codes || []
          },
          sort_by: sortBy,
          sort_order: sortOrder
        })
      });

      const drillDownData = await response.json();
      return drillDownData;
    } catch (error) {
      console.error('Error fetching drill-down data:', error);
      return [];
    }
  };

  const fetchChildData = async (parentNode, childLevel, overrideSortBy = null, overrideSortOrder = null) => {
    const parentPath = parentNode.path || [parentNode.id];
    const parentKey = parentPath.join('.');
    const nodeKey = `${parentKey}.${childLevel}`;
    setLoadingNodes(prev => new Set([...prev, nodeKey]));

    // Determine sort: use override > childSortState > global
    const nodeSortState = childSortState[parentKey];
    const effectiveSortBy = overrideSortBy || nodeSortState?.sortBy || sortBy;
    const effectiveSortOrder = overrideSortOrder || nodeSortState?.sortOrder || sortOrder;

    try {
      let parentFilters = {};
      let requestBody = {};

      if (parentNode['parentPath'] && parentNode.parentPath[0].startsWith('customer_manager_')) {
        if (parentNode.path.length >= 1) parentFilters.customer_account_manager = parentNode.path[0].replace('customer_manager_', '');
        if (parentNode.path.length >= 2) parentFilters.customer_id = parentNode.path[1];
        if (parentNode.path.length >= 3) parentFilters.customer_service_id = parentNode.path[2];
        if (parentNode.path.length >= 4) parentFilters.country_code = parentNode.path[3];
        if (parentNode.path.length >= 5) {
          // Handle mcc_mnc level - split the compound ID
          const [mcc, mnc] = parentNode.path[4].split('-');
          parentFilters.mcc = mcc;
          if (mnc) parentFilters.mnc = mnc;
        }
      } else if (parentNode['parentPath'] && parentNode.parentPath[0].startsWith('vendor_manager_')) {
        if (parentNode.path.length >= 1) parentFilters.vendor_account_manager = parentNode.path[0].replace('vendor_manager_', '');
        if (parentNode.path.length >= 2) parentFilters.vendor_id = parentNode.path[1];
        if (parentNode.path.length >= 3) parentFilters.vendor_service_id = parentNode.path[2];
        if (parentNode.path.length >= 4) parentFilters.country_code = parentNode.path[3];
        if (parentNode.path.length >= 5) {
          // Handle mcc_mnc level - split the compound ID
          const [mcc, mnc] = parentNode.path[4].split('-');
          parentFilters.mcc = mcc;
          if (mnc) parentFilters.mnc = mnc;
        }
      }

      if (parentNode.level === 'customer_account_manager') {
        const managerName = parentNode.name;
        requestBody = {
          filters: {
            time_range: filters.time_range,
            ...((filters.start_date && filters.end_date && filters.end_date >= filters.start_date) && {
              start_date: filters.start_date,
              end_date: filters.end_date
            }),
            customer_ids: filters.customer_ids || [],
            vendor_ids: filters.vendor_ids || [],
            country_codes: filters.country_codes || []
          },
          group_by: ['customer'],
          parent_filters: {
            manager_type: 'customer',
            manager_name: managerName
          },
          sort_by: effectiveSortBy,
          sort_order: effectiveSortOrder,
          limit: 100
        };
      } else if (parentNode.level === 'vendor_account_manager') {
        const managerName = parentNode.name;
        requestBody = {
          filters: {
          time_range: filters.time_range,
            ...((filters.start_date && filters.end_date && filters.end_date >= filters.start_date) && {
              start_date: filters.start_date,
              end_date: filters.end_date
            }),
            customer_ids: filters.customer_ids || [],
            vendor_ids: filters.vendor_ids || [],
            country_codes: filters.country_codes || []
          },
          group_by: ['vendor'],
          parent_filters: {
            manager_type: 'vendor',
            manager_name: managerName
          },
          sort_by: effectiveSortBy,
          sort_order: effectiveSortOrder,
          limit: 100
        };
      } else {
        // For non-AM drills, prefer the overlay-aware parent_context builder
        // (which uses node.pathLevels). Falls back to buildParentFilters for
        // legacy nodes that don't have pathLevels yet.
        if (Array.isArray(parentNode.pathLevels) && parentNode.pathLevels.length > 0) {
          parentFilters = buildParentContextFromPath(parentNode);
        } else if (Object.keys(parentFilters).length === 0) {
          parentFilters = buildParentFilters(parentNode);
        }

        requestBody = {
          filters: {
            time_range: filters.time_range,
            ...((filters.start_date && filters.end_date && filters.end_date >= filters.start_date) && {
              start_date: filters.start_date,
              end_date: filters.end_date
            }),
            customer_ids: filters.customer_ids || [],
            vendor_ids: filters.vendor_ids || [],
            country_codes: filters.country_codes || []
          },
          group_by: [childLevel],
          parent_filters: parentFilters,
          sort_by: effectiveSortBy,
          sort_order: effectiveSortOrder,
          limit: 100
        };
      }

      const response = await apiCall('/api/htable/hierarchy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const childData = await response.json();
      
      // Compute pathLevels for the enriched children: parent's pathLevels
      // (or fallback to the natural chain from rootLevel) + this child's level.
      const parentPathLevels = Array.isArray(parentNode.pathLevels) && parentNode.pathLevels.length > 0
        ? parentNode.pathLevels
        : getPathLevels(parentNode);

      const enrichedChildren = childData.map(child => ({
        ...child,
        path: [...parentPath, child.id],
        level: child.level,
        parentPath: parentPath,
        pathLevels: [...parentPathLevels, child.level],
      }));

      setData(prevData => insertChildrenIntoTree(prevData, parentPath, enrichedChildren));

    } catch (error) {
      console.error('Error fetching child data:', error);
    } finally {
      setLoadingNodes(prev => {
        const newSet = new Set(prev);
        newSet.delete(nodeKey);
        return newSet;
      });
    }
  };
  
  const buildParentFilters = (node) => {
    const filters = {};
    
    if (node['parentPath'] && node.parentPath[0].startsWith('customer_manager')) {
    } else if (node['parentPath'] && node.parentPath[0].startsWith('vendor_manager')) {
    }
    if (node.level === 'customer') {
      filters.customer_id = node.id;
    } else if (node.level === 'vendor') {
      filters.vendor_id = node.id;
    } else if (node.level === 'customer_service') {
      if (node.path.length >= 2) {
        const parentLevel = getParentLevel(node.path);
        if (parentLevel === 'customer') {
          filters.customer_id = node.path[0];
        }
      }
      filters.customer_service_id = node.id;
    } else if (node.level === 'vendor_service') {
      if (node.path.length >= 2) {
        const parentLevel = getParentLevel(node.path);
        if (parentLevel === 'vendor') {
          filters.vendor_id = node.path[0];
        }
      }
      filters.vendor_service_id = node.id;
    } else if (node.level === 'country') {
      const pathContext = buildPathContext(node.path);
      Object.assign(filters, pathContext);
      filters.country_code = node.id;
    } else if (node.level === 'mcc_mnc') {
      const pathContext = buildPathContext(node.path);
      Object.assign(filters, pathContext);
      
      const [mcc, mnc] = node.id.split('-');
      filters.mcc = mcc;
      if (mnc) filters.mnc = mnc;
    } else if (node.level === 'sender_id') {
      const pathContext = buildPathContext(node.path);
      Object.assign(filters, pathContext);
      filters.sender_id = node.id;
    }

    return filters;
  };

  const getParentLevel = (path) => {
    if (path.length === 1) return rootLevel;
    return rootLevel;
  };

  const buildPathContext = (path) => {
    const context = {};
    
    if (rootLevel === 'customer' && path.length >= 1) {
      context.customer_id = path[0];
      if (path.length >= 2) context.customer_service_id = path[1];
      if (path.length >= 3) context.country_code = path[2];
    } else if (rootLevel === 'vendor' && path.length >= 1) {
      context.vendor_id = path[0];
      if (path.length >= 2) context.vendor_service_id = path[1];
      if (path.length >= 3) context.country_code = path[2];
    } else if (rootLevel === 'country' && path.length >= 1) {
      context.country_code = path[0];
    }
    
    return context;
  };

  const insertChildrenIntoTree = (tree, targetPath, children) => {
    if (!targetPath) return tree;
    
    return tree.map(node => {
      const nodePath = node.path || [node.id];
      if (arraysEqual(nodePath, targetPath)) {
        return { ...node, children: children };
      } else if (node.children) {
        return {
          ...node,
          children: insertChildrenIntoTree(node.children, targetPath, children)
        };
      }
      return node;
    });
  };

  const arraysEqual = (a, b) => {
    if (!a || !b) return false;
    return a.length === b.length && a.every((val, index) => val === b[index]);
  };

  const toggleExpand = async (node) => {
    const nodeKey = node.path ? node.path.join('.') : node.id;
    const newExpanded = new Set(expandedNodes);
    
    if (expandedNodes.has(nodeKey)) {
      const toRemove = Array.from(expandedNodes).filter(key => key.startsWith(nodeKey + '.') || key === nodeKey);
      toRemove.forEach(key => newExpanded.delete(key));
    } else {
      newExpanded.add(nodeKey);
      
      if (node.level === 'account_manager_header' && node.children) {
        // Children already loaded for account manager headers
      } else if (!node.children && hasNextLevel(node)) {
        const nextLevel = getNextLevelForNode(node);
        await fetchChildData(node, nextLevel);
      }
    }
    
    setExpandedNodes(newExpanded);
  };

  // Toggle a vendor/customer overlay on this node. The node's children become
  // a different decomposition of the same subtree. We clear cached children
  // and collapse this node + descendants so the next expand refetches with
  // the new overlay applied.
  const toggleOverlay = async (node, mode) => {
    const nodeKey = node.path ? node.path.join('.') : node.id;
    const current = nodeOverlay.get(nodeKey);
    const next = current === mode ? null : mode;

    // 1. Update overlay state
    setNodeOverlay(prev => {
      const m = new Map(prev);
      if (next === null) m.delete(nodeKey);
      else m.set(nodeKey, next);
      return m;
    });

    // 2. Clear cached children at this node so the next expand refetches.
    setData(prev => insertChildrenIntoTree(prev, node.path || [node.id], undefined));

    // 3. Collapse this node and all of its descendants.
    setExpandedNodes(prev => {
      const out = new Set(prev);
      Array.from(out).forEach(k => {
        if (k === nodeKey || k.startsWith(nodeKey + '.')) out.delete(k);
      });
      return out;
    });

    // 4. Auto-expand again so the user sees the result without an extra click.
    //    Defer to next tick so the state updates above land first.
    setTimeout(() => {
      setExpandedNodes(prev => new Set([...prev, nodeKey]));
      // Force the fetch — toggleExpand normally does this, but since we're
      // toggling state directly, replicate the fetch trigger here.
      const nextLevel = (() => {
        // Recompute with the new overlay value (state isn't synchronous).
        if (next === 'vendor') return 'vendor';
        if (next === 'customer') return 'customer';
        const pathLevels = getPathLevels(node);
        let nxt = NATURAL_NEXT[node.level];
        while (nxt && pathLevels.includes(nxt)) nxt = NATURAL_NEXT[nxt];
        return nxt;
      })();
      if (nextLevel) {
        // Build a fresh "node" object reflecting the cleared children, since
        // fetchChildData reads from its argument.
        fetchChildData({ ...node, children: undefined }, nextLevel);
      }
    }, 0);
  };

  const hasNextLevel = (node) => {
    if (node.level === 'account_manager_header') {
      return node.has_children;
    }
    
    if (node.level === 'customer_account_manager' || node.level === 'vendor_account_manager') {
      return true;
    }
    
    const nextLevels = HIERARCHY_LEVELS[node.level];
    return nextLevels && nextLevels.length > 0;
  };

  // Return the path of levels (root → ...→ this node), inclusive of this node.
  // Falls back to a chain reconstructed from rootLevel if node.pathLevels is
  // missing (which happens for nodes loaded before the overlay feature
  // started tagging pathLevels).
  const getPathLevels = (node) => {
    if (Array.isArray(node.pathLevels) && node.pathLevels.length > 0) {
      return node.pathLevels;
    }
    // Fallback — derive from natural chain. Less reliable when overlays
    // were used above but acceptable for legacy nodes.
    if (!node.path) return [node.level];
    const out = [];
    let cur = rootLevel;
    out.push(cur);
    while (out.length < node.path.length && NATURAL_NEXT[cur]) {
      cur = NATURAL_NEXT[cur];
      out.push(cur);
    }
    return out;
  };

  // Compute the level for THIS node's children, given any overlay set on
  // this node and the breadcrumb of levels from root → this node.
  const getNextLevelForNode = (node) => {
    if (node.level === 'customer_account_manager') return 'customer';
    if (node.level === 'vendor_account_manager') return 'vendor';

    const nodeKey = node.path ? node.path.join('.') : node.id;
    const overlay = nodeOverlay.get(nodeKey);
    if (overlay === 'vendor') return 'vendor';
    if (overlay === 'customer') return 'customer';

    const pathLevels = getPathLevels(node);
    let nxt = NATURAL_NEXT[node.level];
    while (nxt && pathLevels.includes(nxt)) {
      nxt = NATURAL_NEXT[nxt];
    }
    return nxt;
  };

  // Build parent_context (column → value) by walking node.path zipped with
  // node.pathLevels. Used when sending a drill request so overlay subtrees
  // are properly scoped.
  const buildParentContextFromPath = (node) => {
    const ctx = {};
    if (!node.path || !node.path.length) return ctx;
    const pathLevels = getPathLevels(node);
    for (let i = 0; i < node.path.length; i++) {
      const lvl = pathLevels[i];
      const id = node.path[i];
      if (!lvl || id == null || id === '') continue;
      if (lvl === 'mcc_mnc') {
        const [mcc, mnc] = String(id).split('-');
        if (mcc) ctx.mcc = mcc;
        if (mnc) ctx.mnc = mnc;
      } else if (LEVEL_KEY[lvl]) {
        ctx[LEVEL_KEY[lvl]] = id;
      }
    }
    return ctx;
  };

  // Decide which overlay buttons to show on a row.
  // - vendor button: hidden if 'vendor' already in path, if user is
  //   customer-scoped, or if the node has no children to overlay (sender_id).
  // - customer button: same with 'customer'.
  const getOverlayButtonsForNode = (node) => {
    if (!node || !node.level) return { vendor: false, customer: false };
    if (node.level === 'sender_id') return { vendor: false, customer: false };
    if (node.level === 'account_manager_header') return { vendor: false, customer: false };
    if (node.level === 'customer_account_manager' || node.level === 'vendor_account_manager') {
      return { vendor: false, customer: false };
    }
    const pathLevels = getPathLevels(node);
    const vendorPossible = !pathLevels.includes('vendor') && !pathLevels.includes('vendor_service');
    const customerPossible = !pathLevels.includes('customer') && !pathLevels.includes('customer_service');
    return {
      vendor: vendorPossible && !isCustomer,
      customer: customerPossible,
    };
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Sort children of a specific expanded parent node independently
  const handleChildSort = useCallback(async (parentNode, column) => {
    const parentPath = parentNode.path || [parentNode.id];
    const parentKey = parentPath.join('.');
    const current = childSortState[parentKey] || { sortBy: sortBy, sortOrder: sortOrder };
    
    let newSortBy = column;
    let newSortOrder = 'desc';
    if (current.sortBy === column) {
      newSortOrder = current.sortOrder === 'asc' ? 'desc' : 'asc';
    }
    
    // Update per-node sort state
    setChildSortState(prev => ({
      ...prev,
      [parentKey]: { sortBy: newSortBy, sortOrder: newSortOrder }
    }));
    
    // Re-fetch children with new sort
    const nextLevel = getNextLevelForNode(parentNode);
    if (nextLevel) {
      await fetchChildData(parentNode, nextLevel, newSortBy, newSortOrder);
    }
  }, [childSortState, sortBy, sortOrder]);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toLocaleString() || '0';
  };

  const formatCurrency = (amount) => {
    return `${formatNumber(amount)}`;
  };

  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(2)}%`;
  };

  const formatDuration = (ms) => {
    if (ms == null) return '0ms';
    if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
    return `${ms.toFixed(0)}ms`;
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />;
  };

  const getLevelIcon = (level) => {
    const icons = {
      customer: '👤',
      account_manager_header: '📊',
      customer_account_manager: '🎯',
      vendor_account_manager: '🎯',
      customer_service: '🔧',
      vendor: '🏢',
      vendor_service: '⚙️',
      country: '🌍',
      mcc_mnc: '📡',
      sender_id: '📱'
    };
    return icons[level] || '📈';
  };

  const getLevelColor = (level) => {
    const colors = {
      customer: 'bg-blue-100 text-blue-800',
      account_manager_header: 'bg-indigo-100 text-indigo-800',
      customer_account_manager: 'bg-green-100 text-green-800',
      vendor_account_manager: 'bg-purple-100 text-purple-800',
      customer_service: 'bg-green-100 text-green-800',
      vendor: 'bg-purple-100 text-purple-800',
      vendor_service: 'bg-orange-100 text-orange-800',
      country: 'bg-red-100 text-red-800',
      mcc_mnc: 'bg-yellow-100 text-yellow-800',
      sender_id: 'bg-gray-100 text-gray-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadge = (deliveryRate) => {
    if (deliveryRate >= 95) return 'bg-green-100 text-green-800';
    if (deliveryRate >= 90) return 'bg-yellow-100 text-yellow-800';
    if (deliveryRate >= 80) return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };

  const CHILD_SORT_OPTIONS = (() => {
    const all = [
      { value: 'total_messages', label: 'Messages' },
      { value: 'delivered_messages', label: 'Delivered' },
      { value: 'failed_messages', label: 'Failed' },
      { value: 'delivery_rate', label: 'Delivery %' },
      { value: 'revenue', label: 'Revenue' },
      { value: 'cost', label: 'Cost' },
      { value: 'profit_margin', label: 'Profit' },
      { value: 'avg_delivery_time', label: 'Latency' },
      { value: 'unique_destinations', label: 'Destinations' },
      { value: 'error_rate', label: 'Error %' },
      { value: 'fake_dlr_count', label: 'Fake DLR' },
      { value: 'lookup_total', label: 'Lookups' },
      { value: 'mo_count', label: 'MO Count' },
    ];
    if (isCustomer) {
      const blocked = new Set(['cost', 'profit_margin', 'fake_dlr_count']);
      return all.filter((o) => !blocked.has(o.value));
    }
    return all;
  })();

  const ChildSortDropdown = ({ parentNode }) => {
    const parentKey = (parentNode.path || [parentNode.id]).join('.');
    const nodeSortState = childSortState[parentKey] || { sortBy: sortBy, sortOrder: sortOrder };

    return (
      <span className="inline-flex items-center ml-2" onClick={(e) => e.stopPropagation()}>
        <select
          value={nodeSortState.sortBy}
          onChange={(e) => handleChildSort(parentNode, e.target.value)}
          className="text-xs border border-indigo-300 rounded pl-1 pr-5 py-0.5 bg-indigo-50 text-indigo-700 font-medium focus:ring-1 focus:ring-indigo-400 focus:outline-none cursor-pointer"
        >
          {CHILD_SORT_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <button
          onClick={(e) => { e.stopPropagation(); handleChildSort(parentNode, nodeSortState.sortBy); }}
          className="ml-0.5 px-1 py-0.5 text-indigo-600 hover:text-indigo-800 rounded hover:bg-indigo-100"
          title={nodeSortState.sortOrder === 'desc' ? 'Descending — click to toggle' : 'Ascending — click to toggle'}
        >
          {nodeSortState.sortOrder === 'desc' ? <SortDesc size={12} /> : <SortAsc size={12} />}
        </button>
      </span>
    );
  };

  const TableRow = ({ node, level = 0 }) => {
    const nodeKey = node.path ? node.path.join('.') : node.id;
    const isExpanded = expandedNodes.has(nodeKey);
    const isLoading = loadingNodes.has(`${nodeKey}.${getNextLevelForNode(node)}`);
    const hasChildren = hasNextLevel(node);
    const canExpand = hasChildren && !isLoading;

    const isHeaderRow = node.level === 'account_manager_header';
    const rowClass = isHeaderRow 
      ? 'border-b-2 border-gray-300 bg-gray-100 font-semibold'
      : 'border-b hover:bg-gray-50';

    return (
      <>
        <tr className={`${rowClass} cursor-pointer hover:bg-blue-50`} onDoubleClick={(e) => handleRowDoubleClick(node, e)}>
          <td className={`px-0 py-0 sticky left-0 ${isHeaderRow ? 'bg-gray-100' : 'bg-white'} border-r z-10`}
              style={{ position: 'sticky', left: 0 }}>
            <div className="px-4 py-3 overflow-hidden" style={{ width: hierarchyColWidth, paddingLeft: `${16 + level * 20}px` }}>
              <div className="flex items-center">
                {canExpand && (
                  <button
                    onClick={() => toggleExpand(node)}
                    className="mr-2 text-gray-500 hover:text-gray-700 focus:outline-none flex-shrink-0"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                    ) : isExpanded ? (
                      <ChevronDown size={16} />
                    ) : (
                      <ChevronRight size={16} />
                    )}
                  </button>
                )}
                
                <span className="mr-2 flex-shrink-0">{getLevelIcon(node.level)}</span>
                
                <div className="flex items-center min-w-0 overflow-hidden">
                  <span className={`${isHeaderRow ? 'font-bold' : 'font-medium'} text-gray-900 mr-2 truncate`}
                        title={node.name}>
                    {node.name}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded flex-shrink-0 whitespace-nowrap ${getLevelColor(node.level)}`}>
                    {node.level?.replace('_', ' ')}
                  </span>
                  {(() => {
                    const buttons = getOverlayButtonsForNode(node);
                    const nodeKey = node.path ? node.path.join('.') : node.id;
                    const active = nodeOverlay.get(nodeKey);
                    return (
                      <>
                        {buttons.vendor && (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleOverlay(node, 'vendor'); }}
                            className={`ml-1 p-1 rounded flex-shrink-0 ${active === 'vendor' ? 'bg-blue-100 text-blue-700' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'}`}
                            title={active === 'vendor' ? 'Vendor overlay active — click to clear' : 'Show vendor breakdown for this row'}
                          >
                            <Truck size={14} />
                          </button>
                        )}
                        {buttons.customer && (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleOverlay(node, 'customer'); }}
                            className={`ml-1 p-1 rounded flex-shrink-0 ${active === 'customer' ? 'bg-blue-100 text-blue-700' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'}`}
                            title={active === 'customer' ? 'Customer overlay active — click to clear' : 'Show customer breakdown for this row'}
                          >
                            <Users size={14} />
                          </button>
                        )}
                      </>
                    );
                  })()}
                  {isExpanded && node.children && node.children.length > 1 && (
                    <ChildSortDropdown parentNode={node} />
                  )}
                </div>
              </div>
            </div>
          </td>

          {/* Message Volume Metrics */}
          <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatNumber(node.total_messages || 0)}</td>
          <td className="px-4 py-3 text-sm text-green-600 text-right">{formatNumber(node.delivered_messages || 0)}</td>
          <td className="px-4 py-3 text-sm text-red-600 text-right">{formatNumber(node.failed_messages || 0)}</td>
          <td className="px-4 py-3 text-sm text-blue-600 text-right">{formatNumber(node.submitted_count || 0)}</td>
          <td className="px-4 py-3 text-sm text-orange-600 text-right">{formatNumber(node.unique_destinations || 0)}</td>

          {/* Rate Metrics */}
          <td className="px-4 py-3 text-sm">
            <div className="flex items-center">
              <div className="w-12 bg-gray-200 rounded-full h-2 mr-2">
                <div 
                  className="bg-green-600 h-2 rounded-full" 
                  style={{ width: `${Math.min(node.delivery_rate || 0, 100)}%` }}
                ></div>
              </div>
              <span className={`text-xs px-2 py-1 rounded ${getStatusBadge(node.delivery_rate || 0)}`}>
                {formatPercentage(node.delivery_rate || 0)}
              </span>
            </div>
          </td>
          <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatPercentage(node.submit_rate || 0)}</td>
          <td className="px-4 py-3 text-sm text-red-600 text-right">{formatPercentage(node.error_rate || 0)}</td>
          <td className="px-4 py-3 text-sm text-yellow-600 text-right">{formatPercentage(node.retry_percentage || 0)}</td>

          {/* Financial Metrics */}
          <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(node.revenue || 0)}</td>
          {!isCustomer && (
            <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(node.cost || 0)}</td>
          )}
          {!isCustomer && (
            <td className="px-4 py-3 text-sm text-right">
              <span className={(node.profit_margin || 0) >= 0 ? 'text-green-600' : 'text-red-600'}>
                {formatCurrency(node.profit_margin || 0)}
              </span>
            </td>
          )}
          {!isCustomer && (
            <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatPercentage(node.margin_percentage || 0)}</td>
          )}
          <td className="px-4 py-3 text-sm text-gray-600 text-right">{formatCurrency(node.revenue_per_message || 0)}</td>
          {!isCustomer && (
            <td className="px-4 py-3 text-sm text-gray-600 text-right">{formatCurrency(node.cost_per_message || 0)}</td>
          )}

          {/* Performance Metrics */}
          <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatDuration(node.avg_submit_time || 0)}</td>
          <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatDuration(node.avg_forwarded_time || 0)}</td>
          <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatDuration(node.avg_delivery_time || 0)}</td>
          
          {/* Quality Metrics */}
          {!isCustomer && (
            <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatPercentage(node.fake_percentage || 0)}</td>
          )}
          {!isCustomer && (
            <td className="px-4 py-3 text-sm text-red-600 text-right">{formatNumber(node.fake_dlr_count || 0)}</td>
          )}
          <td className="px-4 py-3 text-sm text-yellow-600 text-right">{formatNumber(node.total_retry_count || 0)}</td>

          {/* MO / MT (Direction) */}
          <td className="px-4 py-3 text-sm text-cyan-600 text-right">{formatNumber(node.mo_count || 0)}</td>
          <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatPercentage(node.mo_percentage || 0)}</td>

          {/* Geographic & Pattern Metrics */}
          <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatNumber(node.domestic_traffic_count || 0)}</td>
          <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatPercentage(node.domestic_percentage || 0)}</td>
          <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatPercentage(node.minified_url_percentage || 0)}</td>
          <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatNumber(node.minified_url_total || 0)}</td>
          <td className="px-4 py-3 text-sm text-gray-900 text-right">{(node.peak_hour || 0)}:00</td>
          <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatPercentage(node.weekend_percentage || 0)}</td>
          <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatNumber(node.campaign_count || 0)}</td>

          {/* Lookup Metrics */}
          <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatNumber(node.lookup_total || 0)}</td>
          <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatPercentage(node.lookup_percentage || 0)}</td>
        </tr>

        {/* Render children */}
        {isExpanded && node.children && node.children.map((child) => (
          <TableRow 
            key={child.path ? child.path.join('.') : child.id}
            node={child} 
            level={level + 1}
          />
        ))}
      </>
    );
  };

  const DetailPopup = ({ data, onClose, onPageChange, onSort, onFilter, onPageSizeChange, currentPage, currentFilters, pageSize = 100 }) => {
    const filterRefs = useRef({
      message_id: null,
      sender_id: null,
      receiver_id: null,
      customer: null,
      vendor: null,
      country: null,
      network: null
    });

    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
      if (currentFilters) {
        Object.keys(currentFilters).forEach(key => {
          if (filterRefs.current[key]) {
            filterRefs.current[key].value = currentFilters[key] || '';
          }
        });
      }
    }, [currentFilters]);

    if (!data) return null;

    const handleSort = (column) => {
      let newOrder = 'desc';
      if (data.sort_by === column) {
        newOrder = data.sort_order === 'desc' ? 'asc' : 'desc';
      }
      onSort(column, newOrder);
    };

    const handleFilterApply = () => {
      const filters = {};
      Object.keys(filterRefs.current).forEach(key => {
        if (filterRefs.current[key]) {
          filters[key] = filterRefs.current[key].value;
        }
      });
      onFilter(filters);
    };

    const clearAllFilters = () => {
      Object.keys(filterRefs.current).forEach(key => {
        if (filterRefs.current[key]) {
          filterRefs.current[key].value = '';
        }
      });
      onFilter({
        message_id: '',
        sender_id: '',
        receiver_id: '',
        customer: '',
        vendor: '',
        country: '',
        network: ''
      });
    };

    const SortableHeader = ({ column, label, sortable = true }) => {
      const isActive = data.sort_by === column;
      
      return (
        <th className={`px-3 py-2 text-left sticky top-0 ${
          isActive ? 'bg-blue-50' : 'bg-gray-50'
        }`}>
          <div className="space-y-2">
            {sortable ? (
              <button
                onClick={() => handleSort(column)}
                className={`flex items-center space-x-1 text-xs font-semibold uppercase tracking-wider hover:text-blue-600 transition-colors ${
                  isActive ? 'text-blue-700' : 'text-gray-700'
                }`}
              >
                <span>{label}</span>
                {isActive ? (
                  data.sort_order === 'desc' ? 
                    <ChevronDown className="h-3 w-3" /> : 
                    <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3 opacity-30" />
                )}
              </button>
            ) : (
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-700">
                {label}
              </div>
            )}
          </div>
        </th>
      );
    };

    const SearchableHeader = ({ column, label, filterKey, sortable = true }) => {
      const isActive = data.sort_by === column;
      
      return (
        <th className={`px-3 py-2 text-left sticky top-0 ${
          isActive ? 'bg-blue-50' : 'bg-gray-50'
        }`}>
          <div className="space-y-2">
            {sortable && (
              <button
                onClick={() => handleSort(column)}
                className={`flex items-center space-x-1 text-xs font-semibold uppercase tracking-wider hover:text-blue-600 transition-colors ${
                  isActive ? 'text-blue-700' : 'text-gray-700'
                }`}
              >
                <span>{label}</span>
                {isActive ? (
                  data.sort_order === 'desc' ? 
                    <ChevronDown className="h-3 w-3" /> : 
                    <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3 opacity-30" />
                )}
              </button>
            )}
            
            {!sortable && (
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-700">
                {label}
              </div>
            )}
            
            <div className="relative">
              <input
                ref={el => filterRefs.current[filterKey] = el}
                type="text"
                defaultValue={currentFilters?.[filterKey] || ''}
                onBlur={handleFilterApply}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                  }
                }}
                placeholder="Filter..."
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={() => {
                  if (filterRefs.current[filterKey]) {
                    filterRefs.current[filterKey].value = '';
                    handleFilterApply();
                  }
                }}
                className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          </div>
        </th>
      );
    };

    const hasActiveFilters = currentFilters && Object.values(currentFilters).some(v => v !== '');

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-2xl max-w-[95vw] w-full max-h-[95vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50 flex justify-between items-center flex-shrink-0">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Detailed CDR Records
              </h2>
              <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                <span>{data.node_info.name}</span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  Showing
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="mx-1 px-2 py-0.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                  >
                    <option value={100}>100</option>
                    <option value={500}>500</option>
                    <option value={1000}>1000</option>
                  </select>
                  of {data.total_count.toLocaleString()} records
                </span>
                {hasActiveFilters && (
                  <>
                    <span>•</span>
                    <span className="text-blue-600 font-medium">
                      {Object.values(currentFilters).filter(v => v).length} filter(s) active
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Clear All Filters
                </button>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
          
          {/* Table */}
          <div className="flex-1 overflow-auto">
            {data.records && data.records.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <SortableHeader column="event_date" label="Date" />
                    <SortableHeader column="submitted_time" label="Submitted" />
                    <SearchableHeader column="" label="Message ID" filterKey="message_id" sortable={false} />
                    <SearchableHeader column="sender_id" label="Sender" filterKey="sender_id" />
                    <SearchableHeader column="" label="Receiver" filterKey="receiver_id" sortable={false} />
                    <th className="px-3 py-2 text-left sticky top-0 bg-gray-50">
                      <div className="text-xs font-semibold uppercase tracking-wider text-gray-700">
                        Status
                      </div>
                    </th>
                    <SearchableHeader column="customer_name" label="Customer" filterKey="customer" />
                    {!isCustomer && (
                      <SearchableHeader column="vendor_name" label="Vendor" filterKey="vendor" />
                    )}
                    <SearchableHeader column="country_name" label="Country" filterKey="country" />
                    <SearchableHeader column="network_name" label="Network" filterKey="network" />
                    <th className="px-3 py-2 text-left sticky top-0 bg-gray-50">
                      <div className="text-xs font-semibold uppercase tracking-wider text-gray-700">
                        Channel
                      </div>
                    </th>
                    <th className="px-3 py-2 text-left sticky top-0 bg-gray-50">
                      <div className="text-xs font-semibold uppercase tracking-wider text-gray-700">
                        Price
                      </div>
                    </th>
                    {!isCustomer && (
                      <th className="px-3 py-2 text-left sticky top-0 bg-gray-50">
                        <div className="text-xs font-semibold uppercase tracking-wider text-gray-700">
                          Profit
                        </div>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.records.map((record, index) => (
                    <tr key={`${record.message_id}-${index}`} className="hover:bg-blue-50 transition-colors">
                      <td className="px-3 py-3 text-sm text-gray-900 whitespace-nowrap">{record.event_date}</td>
                      <td className="px-3 py-3 text-sm text-gray-600 whitespace-nowrap text-xs">{record.submitted_time}</td>
                      <td className="px-3 py-3 text-sm text-gray-900 font-mono text-xs">{record.message_id}</td>
                      <td className="px-3 py-3 text-sm text-gray-900">{record.sender_id}</td>
                      <td className="px-3 py-3 text-sm text-gray-900">{record.receiver_id}</td>
                      <td className="px-3 py-3 text-sm">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          record.message_status === 'delivered' ? 'bg-green-100 text-green-800' :
                          record.message_status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {record.message_status}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-900">{record.customer_name}</td>
                      {!isCustomer && (
                        <td className="px-3 py-3 text-sm text-gray-900">{record.vendor_name}</td>
                      )}
                      <td className="px-3 py-3 text-sm text-gray-900">{record.country_name}</td>
                      <td className="px-3 py-3 text-sm text-gray-900">{record.network_name}</td>
                      <td className="px-3 py-3 text-sm text-gray-700">
                        <span className="px-2 py-1 bg-gray-100 rounded text-xs">{record.channel}</span>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-900 font-medium">{formatCurrency(record.customer_price)}</td>
                      {!isCustomer && (
                        <td className="px-3 py-3 text-sm font-medium">
                          <span className={record.profit_margin >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {formatCurrency(record.profit_margin)}
                          </span>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <div className="text-6xl mb-4">🔍</div>
                <p className="text-lg font-medium">No records found</p>
                <p className="text-sm">Try adjusting your filters</p>
                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="mt-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {data.total_count > 0 && pageSize < data.total_count && (
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-between items-center flex-shrink-0">
              <div className="text-sm text-gray-700">
                <span className="font-medium">Page {data.current_page}</span> of {data.total_pages} 
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={!data.has_prev}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    data.has_prev 
                      ? 'bg-blue-600 text-white hover:bg-blue-700' 
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={!data.has_next}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    data.has_next 
                      ? 'bg-blue-600 text-white hover:bg-blue-700' 
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Hierarchical CDR Analysis</h1>
        <p className="text-gray-600">
        </p>
      </div>

      <hr className="border-t border-gray-300" />

      {/* Enhanced Filters and Controls */}
      <div className="bg-white rounded-lg shadow mb-6 p-4 mt-8">
        {/* Root Level Selection */}
        <div className="mb-4 pb-4 border-b">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Analysis View
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {ROOT_LEVEL_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => setRootLevel(option.value)}
                className={`p-3 rounded-lg border text-left transition-all ${
                  rootLevel === option.value
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                <div className="font-medium text-sm">{option.label}</div>
                <div className="text-xs text-gray-500 mt-1">{option.description}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div className="flex items-center space-x-2">
            <Filter size={20} className="text-gray-500" />
            <select 
              value={filters.time_range}
              onChange={(e) => setFilters({...filters, time_range: e.target.value})}
              className="border rounded px-3 py-2"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="last_7_days">Last 7 Days</option>
              <option value="last_30_days">Last 30 Days</option>
              <option value="last_90_days">Last 90 Days</option>
              <option value="custom">Custom Range</option>
              </select>
              {filters.time_range === 'custom' && (
                <>
                  <div>
                    <input
                      type="date"
                      value={filters.start_date || ''}
                      max={new Date().toISOString().split('T')[0]} // Prevent future dates
                      onChange={(e) => {
                        const newStartDate = e.target.value;
                        setFilters({
                          ...filters, 
                          start_date: newStartDate,
                          // Clear end_date if it becomes less than start_date
                          ...(filters.end_date && newStartDate > filters.end_date && { end_date: '' })
                        });
                      }}
                      className="border rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <input
                      type="date"
                      value={filters.end_date || ''}
                      min={filters.start_date || undefined} // End date must be >= start date
                      max={new Date().toISOString().split('T')[0]} // Prevent future dates
                      onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                      className="border rounded px-3 py-2"
                      disabled={!filters.start_date} // Disable until start_date is selected
                    />
                  </div>
                  {filters.start_date && filters.end_date && filters.end_date < filters.start_date && (
                    <div className="text-red-500 text-sm col-span-2">
                      End date must be greater than or equal to start date
                    </div>
                  )}
                </>
              )}
          </div>

          <div className="flex items-center space-x-2">
            <Search size={16} className="text-gray-500" />
            <input
              type="text"
              placeholder="Search nodes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border rounded px-3 py-2 w-64"
            />
          </div>

          <button 
            onClick={fetchRootData}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Refresh
          </button>

          <button className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center">
            <Download size={16} className="mr-2" />
            Export
          </button>
        </div>

        {/* Dynamic Hierarchy Legend */}
        <div className="flex flex-wrap gap-2 text-sm">
          <span className="text-gray-600">Current Hierarchy Path:</span>
          <span className={`px-2 py-1 rounded text-xs ${getLevelColor(rootLevel === 'account_manager' ? 'account_manager_header' : rootLevel)}`}>
            {ROOT_LEVEL_OPTIONS.find(opt => opt.value === rootLevel)?.label}
          </span>
          
          {rootLevel === 'account_manager' ? (
            <div className="flex items-center space-x-2">
              <span className="text-gray-400">→</span>
              <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-800">Customer Managers</span>
              <span className="text-gray-400">/</span>
              <span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-800">Vendor Managers</span>
              <span className="text-gray-400">→</span>
              <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">Customers/Vendors</span>
            </div>
          ) : (
            HIERARCHY_LEVELS[rootLevel]?.slice(0, 3).map((level, index) => (
              <React.Fragment key={level}>
                <span className="text-gray-400">→</span>
                <span className={`px-2 py-1 rounded text-xs ${getLevelColor(level)}`}>
                  {getLevelIcon(level)} {level.replace('_', ' ')}
                </span>
              </React.Fragment>
            ))
          )}
          
          {HIERARCHY_LEVELS[rootLevel]?.length > 3 && rootLevel !== 'account_manager' && (
            <span className="text-gray-400">...</span>
          )}
        </div>
      </div>

      {/* Extended Hierarchical Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto" style={{ position: 'relative' }}>
          <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '2600px' }}>
            <thead className="bg-gray-50">
              <tr>
                {/* Sticky Hierarchy Column — resizable */}
                <th className="px-0 py-0 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 border-r z-10 select-none"
                    style={{ position: 'sticky', left: 0 }}>
                  <div className="relative px-4 py-3" style={{ width: hierarchyColWidth, overflow: 'hidden' }}>
                    <div className="flex items-center justify-between">
                      <span>Hierarchy</span>
                      {/*<span className="text-gray-400 text-[10px] ml-1 tabular-nums">{hierarchyColWidth}px</span>*/}
                    </div>
                    {/* Drag handle */}
                    <div
                      onMouseDown={onColResizeStart}
                      onDoubleClick={(e) => { e.stopPropagation(); setHierarchyColWidth(280); }}
                      className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize group flex items-center justify-center"
                      title="Drag to resize · Double-click to reset"
                      style={{ zIndex: 11 }}
                    >
                      <div className="w-0.5 h-4 bg-gray-300 group-hover:bg-blue-500 group-active:bg-blue-600 rounded transition-colors" />
                    </div>
                  </div>
                </th>

                {/* Volume Metrics */}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer bg-gray-200"
                    onClick={() => handleSort('total_messages')}>
                  <div className="flex items-center justify-end">
                    Total Messages
                    <SortIcon column="total_messages" />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">Delivered</div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">Failed</div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">Submitted</div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">Unique Dest.</div>
                </th>

                {/* Rate Metrics */}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer bg-gray-200"
                    onClick={() => handleSort('delivery_rate')}>
                  <div className="flex items-center">
                    Delivery Rate
                    <SortIcon column="delivery_rate" />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">Submit Rate</div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">Error Rate</div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">Retry Rate</div>
                </th>

                {/* Financial Metrics */}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer bg-gray-200"
                    onClick={() => handleSort('revenue')}>
                  <div className="flex items-center justify-end">
                    Revenue
                    <SortIcon column="revenue" />
                  </div>
                </th>
                {!isCustomer && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="text-right">Cost</div>
                  </th>
                )}
                {!isCustomer && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer bg-gray-200"
                      onClick={() => handleSort('profit_margin')}>
                    <div className="flex items-center justify-end">
                      Profit
                      <SortIcon column="profit_margin" />
                    </div>
                  </th>
                )}
                {!isCustomer && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="text-right">Margin %</div>
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">Rev/Msg</div>
                </th>
                {!isCustomer && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="text-right">Cost/Msg</div>
                  </th>
                )}

                {/* Performance Metrics */}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">Avg Submit Time</div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">Avg Forwarded Time</div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">Avg Delivery Time</div>
                </th>

                {/* Quality Metrics */}
                {!isCustomer && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="text-right">Fake %</div>
                  </th>
                )}
                {!isCustomer && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="text-right">Fake DLR Count</div>
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">Retry Count</div>
                </th>

                {/* MO / MT (Direction) */}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">MO Count</div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">MO %</div>
                </th>

                {/* Geographic & Pattern Metrics */}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">Domestic Count</div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">Domestic %</div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">URL Minify %</div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">URL Minify Count</div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">Peak Hour</div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">Weekend %</div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">Campaigns</div>
                </th>

                {/* Lookup Metrics */}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">Lookup Count</div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="text-right">Lookup %</div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="29" className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className="ml-2">Loading hierarchy...</span>
                    </div>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan="29" className="px-6 py-4 text-center text-gray-500">
                    No data found. Try adjusting your filters.
                  </td>
                </tr>
              ) : (
                data
                  .filter(item => 
                    !searchTerm || 
                    item.name.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((node) => (
                    <TableRow key={node.path ? node.path.join('.') : node.id} node={node} />
                  ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls for Root Data */}
        { rootLevel == 'sender_id' && (  
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-700">
                Showing max 10000 results
              </div>
              <div className="text-sm text-gray-700 hidden">
                Showing <span className="font-medium">{rootOffset + 1}</span> to{' '}
                <span className="font-medium">{Math.min(rootOffset + rootLimit, totalRootRecords)}</span> of{' '}
                <span className="font-medium">{totalRootRecords}</span> results
              </div>
              
              <div className="flex items-center space-x-2 hidden">
                <label htmlFor="pageSize" className="text-sm text-gray-700">
                  Per page:
                </label>
                <select
                  id="pageSize"
                  value={rootLimit}
                  onChange={(e) => {
                    setRootLimit(parseInt(e.target.value));
                    setRootOffset(0); // Reset to first page
                  }}
                  className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={500}>500</option>
                </select>
              </div>
            </div>

            <div className="flex items-center space-x-2 hidden">
              <button
                onClick={() => setRootOffset(Math.max(0, rootOffset - rootLimit))}
                disabled={rootOffset === 0}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <div className="text-sm text-gray-700">
                Page {Math.floor(rootOffset / rootLimit) + 1} of {Math.ceil(totalRootRecords / rootLimit)}
              </div>
              
              <button
                onClick={() => setRootOffset(rootOffset + rootLimit)}
                disabled={rootOffset + rootLimit >= totalRootRecords}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
      </div>

     {showDetailPopup && (
        <DetailPopup
          data={detailData}
          onClose={() => {
            setShowDetailPopup(false);
            setDetailData(null);
            setCurrentPage(1);
            setSearchFilters({
              message_id: '',
              sender_id: '',
              receiver_id: '',
              customer: '',
              vendor: '',
              country: '',
              network: ''
            });
            setPageSize(100);
          }}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSort={handleDetailedSort}
          onFilter={handleFilter}
          currentPage={currentPage}
          currentFilters={searchFilters}
          pageSize={pageSize}
        />
      )}

      {loadingDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-2">Loading detailed records...</span>
            </div>
          </div>
        </div>
      )}

      <div className="fixed bottom-4 right-4 z-40">
        <SchedulerWidget isVisible={true} position="static"/>
      </div>
    </div>
  );
};

export default HierarchicalTable;